import "./DataItem.less";

import { Button, Card, Input } from "antd";
import React from "react";
import Highlighter from "react-highlight-words";

import { IBaseProps, IForm, IHighlightProps } from "lib/data/interfaces";
import {
	AggregateDataFieldValue,
	DataFieldTemplate,
	DataFieldValue,
	LinkDataField,
	LinkDataFieldValue,
	ListDataFieldValue,
	OrderedDataFieldValue,
	Permission,
	StringDataFieldValue,
	TextDataFieldValue
} from "lib/data/model";
import { LoginService, PermissionService, StateService } from "lib/services";

import { DataItemForm } from "components/forms";
import { CancellationToken } from "lib/api/base";


interface IDataItemProps extends IBaseProps, IHighlightProps {
	itemKey?: string | number;
	template: DataFieldTemplate;
	value?: DataFieldValue;
	showTitle?: boolean;
	canEditTitle?: boolean;
	requiredPermission?: Permission;
	isRequired?: boolean;
	isEditing?: boolean;
	isSaving?: boolean;
	isEditable?: boolean;
	onSave?: (result?: DataFieldValue) => Promise<boolean | void>;
	actions?: Array<React.ReactElement<Button>>;
}

interface IDataItemState {
	isEditable: boolean;
	isEditing: boolean;
	isSaving: boolean;
	isFormValid: boolean;
	labelValue?: string;
}


export class DataItem extends React.Component<IDataItemProps, IDataItemState> {

	public static defaultProps = {
		showTitle: true,
		isSaving: false
	};

	private apiCancellationToken: CancellationToken = new CancellationToken();

	private value?: DataFieldValue;

	private formReference?: IForm<DataFieldValue>;

	constructor(props: IDataItemProps) {

		super(props);

		this.state = {
			isEditable: false,
			isEditing: false,
			isSaving: false,
			isFormValid: true
		};
	}

	public async componentWillMount() {

		// Set Optional Prop Defaults
		const isEditing: boolean = (this.props.isEditing !== undefined ? this.props.isEditing : false);
		const isSaving: boolean = (this.props.isSaving !== undefined ? this.props.isSaving : false);

		// Store Value
		this.value = this.props.value;

		// Check For Saving
		if (isEditing && isSaving) {

			// Save Record
			this.saveEdit();
		}
		else if (this.props.isEditable !== undefined
			&& this.props.isEditable
			&& this.props.onSave !== undefined) {

			// Check Permission
			if (this.props.requiredPermission) {

				// Get Logged In User
				const loggedInUser = await LoginService.getUserIfExists();

				// Check Logged In User
				if (loggedInUser) {

					// Check Permission
					const isPermitted = await PermissionService.userHasPermission(loggedInUser, this.props.requiredPermission);

					// Indicate If Data Item Is Editable
					StateService.setCancelableState(() => this.setState({ isEditable: isPermitted, isEditing }), this.apiCancellationToken);
				}
			}
			else {

				// Indicate Data Item Is Editable
				StateService.setCancelableState(() => this.setState({ isEditable: true, isEditing }), this.apiCancellationToken);
			}
		}
		else {

			// Indicate Data Item Is NOT Editable
			StateService.setCancelableState(() => this.setState({ isEditable: false, isEditing: false }), this.apiCancellationToken);
		}
	}

	public async componentWillReceiveProps(newProps: IDataItemProps) {

		// Set Optional Prop Defaults
		const isEditing: boolean = (newProps.isEditing !== undefined ? newProps.isEditing : false);
		const isSaving: boolean = (newProps.isSaving !== undefined ? newProps.isSaving : false);

		// Store Value
		this.value = newProps.value;

		// Check For Saving
		if (isEditing && isSaving) {

			// Save Record
			this.saveEdit();
		}
		else if (newProps.isEditable !== undefined
			&& newProps.isEditable
			&& newProps.onSave !== undefined) {

			// Check Permission
			if (newProps.requiredPermission) {

				// Get Logged In User
				const loggedInUser = await LoginService.getUserIfExists();

				// Check Logged In User
				if (loggedInUser) {

					// Check Permission
					const isPermitted = await PermissionService.userHasPermission(loggedInUser, newProps.requiredPermission);

					// Indicate If Data Item Is Editable
					StateService.setCancelableState(() => this.setState({ isEditable: isPermitted, isEditing, isSaving: (!isEditing ? false : isSaving) }), this.apiCancellationToken);
				}
			}
			else {

				// Indicate Data Item Is Editable
				StateService.setCancelableState(() => this.setState({ isEditable: true, isEditing, isSaving: (!isEditing ? false : isSaving) }), this.apiCancellationToken);
			}
		}
		else {

			// Indicate Data Item Is NOT Editable
			StateService.setCancelableState(() => this.setState({ isEditable: false, isEditing: false, isSaving: false }), this.apiCancellationToken);
		}
	}

	public componentWillUnmount() {

		// Cancel Outstanding API Calls
		this.apiCancellationToken.cancel();
	}

	public render() {

		// Define Class Name
		const className = [
			"data-field-item",
			(this.state.isEditing ? "data-item-editing" : undefined),
			this.props.className
		]
			.filter((c) => c !== undefined)
			.join(" ")
			.trim();

		// Initialize Data Field Name
		let itemName: React.ReactNode;

		// Initialize Data Field Value
		let itemComponent: React.ReactNode = null;

		// Check For Editing
		if (this.state.isEditing) {

			itemComponent = (
				<DataItemForm
					wrappedComponentRef={this.setFormReference}
					template={this.props.template}
					value={this.value}
					onValidated={this.onFormValidated}
					isRequired={this.props.isRequired}
				/>
			);
		}
		else {

			// Check For Empty Value
			if (this.value !== undefined) {

				// Check Data Item Type
				switch (this.value.$type) {

					case StringDataFieldValue.$type:

						// Build Data Field Value Component
						itemComponent = this.buildStringValue(this.value as StringDataFieldValue, false, this.props.itemKey);

						break;

					case ListDataFieldValue.$type:

						// Build Data Field Value Component
						itemComponent = this.buildListValue(this.value as ListDataFieldValue, this.props.itemKey);

						break;

					case AggregateDataFieldValue.$type:

						// Cast Data Field Value
						const aggregateFieldValue = this.value as AggregateDataFieldValue;

						// Adjust Data Field Name
						aggregateFieldValue.template.field.name = aggregateFieldValue.template.field.name.replace(/aggregate/gi, "").trim();

						// Build Data Field Value Component
						itemComponent = this.buildAggregateValue(aggregateFieldValue, this.props.itemKey);

						break;

					case TextDataFieldValue.$type:

						// Build Data Field Value Component
						itemComponent = this.buildTextValue(this.value as TextDataFieldValue, this.props.itemKey);

						break;
				}
			}
		}

		// Initialize Actions
		const actions: Array<React.ReactElement<Button>> = [];

		// Check For Need To Show Data Field Name
		if (this.props.showTitle) {

			// Build Data Field Name Component
			// Standard view for any Item with a title(targeting metadata/headerdata)
			itemName = <span className="data-field-item-title">{this.props.template.field.name}</span>;


			// Check Current State (For Actions)
			if (this.state.isEditable && !this.state.isEditing) {

				actions.push(
					(
						<Button
							key={actions.length}
							type="ghost"
							icon="edit"
							onClick={this.toggleEditing}
						/>
					)
				);

				// Check For Provided Actions
				if (this.props.actions !== undefined) {

					// Add Provided Actions
					actions.push(...this.props.actions);
				}
			}
			else if (this.state.isEditing) {

				actions.push(
					<Button
						key={actions.length}
						type="default"
						icon="check"
						loading={this.state.isSaving}
						onClick={this.saveEdit}
						disabled={(this.state.isFormValid ? false : true)}
					/>
				);

				actions.push(
					<Button
						key={actions.length}
						type="danger"
						icon="close"
						disabled={this.state.isSaving}
						onClick={this.toggleEditing}
					/>
				);
			}
		}

		return (

			<Card
				className={className}
				type="inner"
				bordered={false}
				size="small"
				title={(
					this.props.showTitle
						?
						(
							this.props.canEditTitle && this.state.isEditing
								?
								(
									<Input
										value={this.state.labelValue}
										onChange={(event) => this.setState({ labelValue: event.target.value })}
									/>
								)
								:
								itemName
						)
						:
						undefined
				)}
				extra={(
					this.props.showTitle
						?
						<Button.Group className="data-item-actions">
							{actions}
						</Button.Group>
						:
						undefined
				)}
			>
				{itemComponent}
			</Card>
		);
	}

	private setFormReference = (form: IForm<DataFieldValue>) => {

		// Set Reference
		this.formReference = form;
	}

	private onFormValidated = (isValid: boolean) => {

		// Check If Value Changed
		if (isValid !== this.state.isFormValid) {

			// Indicate Whether Form Validation Error Occurred
			StateService.setCancelableState(() => this.setState({ isFormValid: isValid }), this.apiCancellationToken);
		}
	}

	private saveEdit = async () => {

		// Ensure Saving Is Occurring
		StateService.setCancelableState(() => this.setState({ isSaving: true }), this.apiCancellationToken);

		// Check For: Save Handler, Correct State, Form Reference
		if (this.props.onSave && this.state.isEditing && this.formReference) {

			// Save Form
			this.value = this.formReference.save();

			if (this.value !== undefined && this.state.labelValue !== undefined) {

				this.value.template.field.name = this.state.labelValue;
			}

			// Save Changes To Record
			const isSaved = await this.props.onSave(this.value);

			// Check If Editing Was Enabled By This Component (Not By A Parent)
			if (typeof isSaved === "boolean" && this.props.isEditing === undefined) {

				// Check If Saved
				if (isSaved) {

					// Reset State
					StateService.setCancelableState(() => this.setState({ isSaving: false, isEditing: false }), this.apiCancellationToken);
				}
				else {

					// Reset State
					StateService.setCancelableState(() => this.setState({ isSaving: false }), this.apiCancellationToken);
				}
			}
		}
	}

	private toggleEditing = () => {

		// Trigger Editing
		StateService.setCancelableState(
			() => this.setState({
				isEditing: !this.state.isEditing,
				labelValue: this.state.isEditing ? undefined : this.props.template.field.name
			}
		), this.apiCancellationToken);
	}

	private buildStringValue = (stringValue: StringDataFieldValue, isInAggregate: boolean = false, key?: string | number): React.ReactNode => {

		// Initialize String Value Component
		let component: React.ReactNode;

		// Initialize Value
		let processedValue: string;

		// Check For Template
		if (stringValue.template) {

			// Build Complete Value
			processedValue = [
				stringValue.template.stringPrefix,
				stringValue.value,
				stringValue.template.stringAppendage
			].filter((r) => r !== undefined && r !== null).join("").trim();
		}
		else {

			// Set Processed Value
			processedValue = stringValue.value;
		}

		// Check If Value Is Link
		if (!isInAggregate
			&& stringValue.template !== undefined
			&& LinkDataField.isLinkField(stringValue.template.field)
			&& (stringValue.template.field as LinkDataField).hasLink
			&& stringValue.protocol !== undefined) {

			// Get Link Destination
			const destination = (LinkDataFieldValue.isLinkValue(stringValue) ? (stringValue as LinkDataFieldValue).destination : (stringValue as StringDataFieldValue).value);

			// Build Data Field Value Component
			component = (
				<div
					key={key}
					className="data-field-item-string-value"
				>

					<a
						href={`${stringValue.protocol!.protocol}${destination}`}
						target="_blank"
					>

						{(this.props.highlightWords
							?
							<Highlighter searchWords={[this.props.highlightWords.join(" ")]} textToHighlight={processedValue} />
							:
							processedValue
						)}
					</a>
				</div>
			);
		}
		else {

			// Build Data Field Value Component
			component = (
				<div key={key} className="data-field-item-string-value">
					<span>
						{(this.props.highlightWords
							?
							<Highlighter searchWords={[this.props.highlightWords.join(" ")]} textToHighlight={processedValue} />
							:
							processedValue
						)}
					</span>
				</div>
			);
		}

		// Return Component
		return component;
	}

	private buildListValue = (listValue: ListDataFieldValue, key?: string | number): React.ReactNode => {

		// Initialize String Value Component
		let component: React.ReactNode;

		// Initialize Value
		let processedValue: string;

		// Check For Value
		if (listValue !== undefined && listValue.value !== undefined) {

			// Check For Template
			if (listValue.template) {

				// Build Complete Value
				processedValue = [
					listValue.template.stringPrefix,
					listValue.value.value,
					listValue.template.stringAppendage
				].filter((r) => r !== undefined && r !== null).join("").trim();
			}
			else {

				// Set Processed Value
				processedValue = listValue.value.value;
			}
		}
		else {

			// Set Processes Value (Empty)
			processedValue = "";
		}

		// Build Data Field Value Component
		component = (
			<div key={key} className="data-field-item-list-value">
				<span>
					{(this.props.highlightWords
						?
						<Highlighter searchWords={[this.props.highlightWords.join(" ")]} textToHighlight={processedValue} />
						:
						processedValue
					)}
				</span>
			</div>
		);

		// Return Component
		return component;
	}

	private buildAggregateValue = (aggregateValue: AggregateDataFieldValue, key?: string | number): React.ReactNode => {

		// Initialize Aggregate Field Value Components
		const components: React.ReactNode[] = [];

		// Check For Sub-Values
		if (aggregateValue.values !== undefined && aggregateValue.values !== null) {

			// Sort Values
			const sortedValues = aggregateValue.values.sort(OrderedDataFieldValue.sort);

			// Loop Through Sorted Aggregate Field Values
			for (let i = 0; i < sortedValues.length; i++) {

				// Check Data Item Type
				switch (sortedValues[i].item.$type) {

					case StringDataFieldValue.$type:

						// Build Data Field Value Component
						components.push(this.buildStringValue(sortedValues[i].item as StringDataFieldValue, true, (sortedValues[i].item.id ? sortedValues[i].item.id : `new${i}`)));

						break;

					case ListDataFieldValue.$type:

						// Build Data Field Value Component
						components.push(this.buildListValue(sortedValues[i].item as ListDataFieldValue, (sortedValues[i].item.id ? sortedValues[i].item.id : `new${i}`)));

						break;

					case AggregateDataFieldValue.$type:

						// Build Data Field Value Component
						const component = (
							<div key={key} className={(this.state.isEditing ? "data-field-item-aggregate-input" : "data-field-item-aggregate-value")}>
								{this.buildAggregateValue(sortedValues[i].item as AggregateDataFieldValue, (sortedValues[i].item.id ? sortedValues[i].item.id : `new${i}`))}
							</div>
						);

						// Add Component
						components.push(component);

						break;

					case TextDataFieldValue.$type:

						// Build Data Field Value Component
						components.push(this.buildTextValue(sortedValues[i].item as TextDataFieldValue, (sortedValues[i].item.id ? sortedValues[i].item.id : `new${i}`)));

						break;
				}
			}
		}

		// Return Components
		return components;
	}

	private buildTextValue = (textValue: TextDataFieldValue, key?: string | number): React.ReactNode => {

		// Initialize Text Value Component
		let component: React.ReactNode;

		// Initialize Value
		let processedValue: string;

		// Check For Template
		if (textValue.template) {

			// Build Complete Value
			processedValue = [
				textValue.template.stringPrefix,
				textValue.value,
				textValue.template.stringAppendage
			].filter((r) => r !== undefined && r !== null).join("").trim();
		}
		else {

			// Set Processed Value
			processedValue = textValue.value;
		}

		// Build Data Field Value Component
		component = (
			<div key={key} className="data-field-item-text-value">
				<span>
					{(this.props.highlightWords
						?
						<Highlighter searchWords={[this.props.highlightWords.join(" ")]} textToHighlight={processedValue} />
						:
						processedValue
					)}
				</span>
			</div>
		);

		// Return Component
		return component;
	}
}
